import Logo from '@atoms/Logo';
import StickyButton from '@atoms/StickyButton';
import { PHONE_NUMBER, PHONE_NUMBER_DISPLAY } from '@constants/constants';
import { useLocator } from '@contexts/franchise/locator-provider.tsx';
import formatPhoneLink from '@utils/format-phone-link';
import React, { useEffect, useState } from 'react';
import Link from '../primitives/link';
import ChangeLocationDrawer from '../franchise/change-location-drawer';
import GlobalNav from './global-nav';
import { useCtaProvider } from '../../contexts/franchise/cta-provider';
import { useMicrosite } from '../../contexts/franchise/microsite-context';
import ArrowDropdown from '../../images/svg/arrow-header-dropdown.svg';
import NavIcon from '../../images/svg/header-nav.svg';
import FranchiseTopNav from './franchise-top-nav';
import HeaderDropdown from './header-dropdown';
import DropdownMenu, { dropdownStyles } from '../primitives/dropdown-menu';
import Skeleton from '../primitives/skeleton';
import Typography from '../primitives/typography';
import ColSet from '../primitives/grid/col-set';
import { colors } from '../primitives/colors';
import Container from '../primitives/grid/container';
import Row from '../primitives/grid/row';
import Col from '../primitives/grid/col';
import PhoneNumber from '../molecules/phone-number';

interface DropdownMenuProps {
    text: string
    phoneNumber?: string
    phoneDisplay?: string
    localCtaData?: any
}

const StyledDropdown = ({
    text,
    phoneNumber,
    phoneDisplay,
    localCtaData,
}: DropdownMenuProps): JSX.Element => (
    <DropdownMenu
        align="end"
        title={(
            <ColSet
                breakpoints={{
                    dt: { between: 10 },
                }}
                css={{
                    display: 'flex',
                    position: 'relative',
                    flexDirection: 'row',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    height: 'auto',
                }}
            >
                <Typography
                    fontSize="sm"
                    css={{
                        textAlign: 'right',
                    }}
                >
                    {text}
                    <Skeleton>
                        {phoneNumber && (
                            <PhoneNumber css={styles.phoneLink()} href={`tel:${phoneNumber}`}>
                                {formatPhoneLink(phoneNumber || phoneDisplay)}
                            </PhoneNumber>
                        )}
                    </Skeleton>
                </Typography>
                <ArrowDropdown
                    css={{
                        transition: '0.2s',
                        '[data-state="open"] &': {
                            transform: 'rotate(180deg)',
                        },
                    }}
                />
            </ColSet>
        )}
    >
        <HeaderDropdown
            localCtaData={localCtaData}
        />
    </DropdownMenu>
);

const Header = ({
    alwaysFixed,
    small,
    localCtaData,
    handleChangeLocation,
    hiddenForced,
    title,
}): JSX.Element => {
    const {
        isLocalCityPage,
        isMicrosite,
        permalink: micrositePermalink,
        franchise: micrositeFranchise,
    } = useMicrosite();

    const {
        hideNationalCallCenter,
        hideFranchiseLink,
        overridePhoneNumber,
        overridePhoneNumberDescriptor,
    } = useCtaProvider();

    const { franchise: locatorFranchise, isNationalCallCenter } = useLocator();
    const franchise = localCtaData?.franchise || micrositeFranchise || locatorFranchise?.data;

    const headerEl = React.useRef();

    const [isGlobalNavVisible, setIsGlobalNavVisible] = useState(false);
    const [locationChangeVisible, setLocationChangeVisible] = useState(false);
    const [truncateFranchiseName, setTruncateFranchiseName] = useState(true);
    useEffect(() => {
        document.documentElement.style.overflow = isGlobalNavVisible ? 'hidden' : '';
    }, [isGlobalNavVisible]);

    const toggleTruncate = (): void => {
        const newState = !truncateFranchiseName;
        setTruncateFranchiseName(newState);
    };

    const positionRef = React.useRef(0);
    const visibleRef = React.useRef(true);
    const [visible, setVisible] = React.useState(true);

    React.useEffect(() => {
        const hEl = headerEl.current;
        const { top, height } = hEl.getBoundingClientRect();
        const topZ = top + height + window.pageYOffset;
        const handleScroll = (): void => {
            const moving = window.pageYOffset;
            const isVis = positionRef.current > moving || moving < topZ;
            if (visibleRef.current !== isVis) {
                visibleRef.current = isVis;
                setVisible(isVis);
            }
            positionRef.current = moving;
        };
        window.addEventListener('scroll', handleScroll);
        return () => {
            window.removeEventListener('scroll', handleScroll);
        };
    }, []);

    return (
        <>
            <div
                ref={headerEl}
                style={{
                    transform: (visible || alwaysFixed) ? 'translateY(0%)' : 'translateY(-100%)',
                    display: hiddenForced ? 'none' : 'block',
                    position: 'sticky',
                    top: '0',
                    transition: '0.3s',
                    zIndex: 100,
                }}
            >
                {isMicrosite && (
                    <FranchiseTopNav currentPageTitle={title} />
                )}
                <header css={{
                    width: '100%',
                    backgroundColor: colors.shadesWhite,
                    padding: small ? '8px 0' : '32px 0',
                    '@media (max-width: 1200px)': {
                        padding: '16px 0',
                    },
                    '@media (max-width: 767px)': {
                        paddingBottom: '0',
                    },
                }}
                >
                    <Container css={{
                        display: 'flex',
                        justifyContent: 'space-between',
                        alignItems: 'center',
                    }}
                    >
                        <Row>
                            <Col
                                breakpoints={{ dt: { span: 6, align: 'center', justify: 'start', dir: 'row' }, tb: { span: 3, align: 'center', justify: 'start', dir: 'row' } }}
                                css={{
                                    '@media (max-width: 1024px)': {
                                        gridColumn: '1 / span 10',
                                    },
                                }}
                            >
                                <div css={{
                                    width: small ? '256px' : '288px',
                                    '@media (max-width: 1200px)': {
                                        width: '208px',
                                    },
                                }}
                                >
                                    <Logo to={isMicrosite ? micrositePermalink : '/'} />
                                </div>
                                {isMicrosite && franchise?.name && (
                                    <div css={styles.franchiseNameTablet}>
                                        <h2
                                            css={[styles.franchiseName, truncateFranchiseName && styles.franchiseNameTruncate]}
                                            onClick={toggleTruncate}
                                        >
                                            {franchise?.name}
                                        </h2>
                                    </div>
                                )}
                            </Col>
                            <Col
                                breakpoints={{
                                    dt: {
                                        span: 6, align: 'center', justify: 'end', dir: 'row',
                                    },
                                    tb: {
                                        span: 9, align: 'center', justify: 'end', dir: 'row',
                                    },
                                    mb: { span: 1, align: 'center', justify: 'end', dir: 'row' },
                                }}
                                css={{
                                    '@media (max-width: 1024px)': {
                                        gridColumn: '11 / span 2',
                                    },
                                }}
                            >
                                <div css={styles.flexItemCenter}>
                                    <div css={styles.desktopWrap}>
                                        {!hideFranchiseLink && !hideNationalCallCenter && (
                                            <p
                                                css={styles.phoneLargeLable(isMicrosite, small)}
                                            >
                                                {(isMicrosite && franchise?.tempOutOfService) ? 'National Call Center' : 'Call Today - 24/7 Emergency Services'}
                                            </p>
                                        )}
                                        {overridePhoneNumber && (
                                            <Link css={dropdownStyles} to={`tel:${overridePhoneNumber}`}>
                                                <ColSet
                                                    breakpoints={{
                                                        dt: { between: 10 },
                                                    }}
                                                    css={{
                                                        display: 'flex',
                                                        position: 'relative',
                                                        flexDirection: 'row',
                                                        alignItems: 'center',
                                                        justifyContent: 'space-between',
                                                        height: 'auto',
                                                    }}
                                                >
                                                    <Typography
                                                        fontSize="sm"
                                                        css={{
                                                            textAlign: 'right',
                                                        }}
                                                    >
                                                        {overridePhoneNumberDescriptor}
                                                        <Skeleton>
                                                            <span css={styles.phoneLink(isMicrosite)}>
                                                                {overridePhoneNumber}
                                                            </span>
                                                        </Skeleton>
                                                    </Typography>
                                                </ColSet>
                                            </Link>
                                        )}

                                        <div css={styles.headerCol}>
                                            <ColSet
                                                breakpoints={{ dt: { between: 12 } }}
                                                css={{
                                                    alignItems: 'center',
                                                    justifyContent: 'flex-end',
                                                }}
                                            >
                                                {!isMicrosite
                                                    && !hideFranchiseLink
                                                    && !isNationalCallCenter
                                                    && (locatorFranchise?.data?.isLoading || !locatorFranchise) && (
                                                    <StyledDropdown
                                                        text="Finding your Local SERVPRO"
                                                    />
                                                )}
                                                {((!hideFranchiseLink
                                                    && !isMicrosite
                                                    && !isNationalCallCenter) || (isLocalCityPage && !hideFranchiseLink)
                                                ) && (
                                                    <StyledDropdown
                                                        text="Your Local SERVPRO"
                                                        phoneNumber={franchise.mainPhone}
                                                        phoneDisplay={franchise.mainPhone}
                                                        localCtaData={localCtaData}
                                                    />
                                                )}
                                                {(!hideFranchiseLink && !isMicrosite && isNationalCallCenter
                                                    && !isLocalCityPage) && (
                                                    <StyledDropdown
                                                        text="National Call Center"
                                                        phoneNumber={PHONE_NUMBER}
                                                        phoneDisplay={PHONE_NUMBER_DISPLAY}
                                                    />
                                                )}
                                                {((!hideFranchiseLink
                                                    && !hideNationalCallCenter
                                                    && !isMicrosite
                                                    && !isNationalCallCenter)
                                                    || (isLocalCityPage && !hideNationalCallCenter && !hideFranchiseLink)
                                                ) && (
                                                    <hr
                                                        aria-orientation="vertical"
                                                        css={{
                                                            height: '3.5rem',
                                                            display: 'block',
                                                            backgroundColor: colors.grey200,
                                                            width: '1px',
                                                        }}
                                                    />
                                                )}
                                                {((!hideNationalCallCenter && !isMicrosite && !isNationalCallCenter)
                                                    || (isLocalCityPage && !hideNationalCallCenter)
                                                ) && (
                                                    <Link css={dropdownStyles} to={`tel:${PHONE_NUMBER}`}>
                                                        <div
                                                            css={{
                                                                display: 'flex',
                                                                position: 'relative',
                                                                flexDirection: 'row',
                                                                alignItems: 'center',
                                                                justifyContent: 'space-between',
                                                                height: 'auto',
                                                            }}
                                                        >
                                                            <Typography
                                                                fontSize="sm"
                                                                css={{
                                                                    textAlign: 'right',
                                                                }}
                                                            >
                                                                <span>National Call Center</span>
                                                                <span css={styles.phoneLink(isMicrosite)}>
                                                                    {PHONE_NUMBER_DISPLAY}
                                                                </span>
                                                            </Typography>
                                                        </div>
                                                    </Link>
                                                )}
                                                {isMicrosite && (
                                                    <aside css={styles.contactInfo}>
                                                        <div css={styles.flexItemCenter}>
                                                            <PhoneNumber
                                                                css={styles.phoneLink(isMicrosite)}
                                                                className="hover-default"
                                                                href={`tel:${franchise?.tempOutOfService ? PHONE_NUMBER : franchise.mainPhone}`}
                                                            >
                                                                {franchise?.tempOutOfService ? PHONE_NUMBER_DISPLAY : formatPhoneLink(franchise.mainPhone)}
                                                            </PhoneNumber>
                                                        </div>
                                                    </aside>
                                                )}
                                            </ColSet>
                                        </div>
                                    </div>
                                    <button
                                        className="hover-default"
                                        onClick={() => setIsGlobalNavVisible(current => !current)}
                                    >
                                        <NavIcon />
                                        <span css={styles.menuText}>Menu</span>
                                    </button>
                                </div>
                            </Col>
                        </Row>
                    </Container>
                    {isMicrosite && franchise?.name && (
                        <div css={styles.franchiseNameMobile}>
                            <h2
                                css={[styles.franchiseName, truncateFranchiseName && styles.franchiseNameTruncate]}
                                onClick={toggleTruncate}
                            >
                                {franchise?.name}
                            </h2>
                        </div>
                    )}
                </header>
            </div>
            <ChangeLocationDrawer
                visible={locationChangeVisible}
                onClose={() => setLocationChangeVisible(false)}
            />
            <GlobalNav
                onClose={() => setIsGlobalNavVisible(current => !current)}
                isVisible={isGlobalNavVisible}
            />
            <StickyButton
                handleChangeLocation={handleChangeLocation}
                localCtaData={localCtaData}
                inverse={isGlobalNavVisible}
            />
        </>
    );
};

const styles = {
    phoneLargeLable: (isMicrosite, small) => ({
        marginBottom: small ? '0.25rem' : '0.5rem',
        textAlign: 'center',
        color: colors.primaryOrange,
        fontSize: isMicrosite ? '1.25rem' : '1.125rem',
        lineHeight: '1.75rem',
    }),
    desktopWrap: {
        display: 'none',
        marginRight: '4rem',
        '@media (min-width: 1024px)': {
            display: 'block',
        },
    },
    flexItemCenter: {
        display: 'flex',
        alignItems: 'center',

    },
    headerCol: {
        display: 'flex',
        justifyContent: 'flex-end',
        alignItems: 'center',
        maxWidth: '100%',
    },
    contactInfo: {
        display: 'none',
        flexDirection: 'column',
        fontSize: '0.875rem',
        lineHeight: '1.25rem',
        textAlign: 'right',
        '@media (min-width: 1024px)': {
            display: 'flex',
        },
    },
    phoneLink: (isMicrosite?: boolean) => ({
        display: 'block',
        fontWeight: 500,
        fontSize: isMicrosite ? '1.25rem' : '1.125rem',
        lineHeight: '1.75rem',
        color: !isMicrosite && colors.primaryOrange,
        '&.hover-default': isMicrosite && {
            transition: 'all .1s linear',
            '&:hover': {
                opacity: 1,
                transition: 'all .1s linear',
                transform: 'scale(1.08)',
                color: colors.shadesBlack,
            },
        },
    }),
    franchiseName: {
        overflow: 'hidden',
        fontSize: '1.25rem',
        lineHeight: 'normal',
        fontWeight: 600,
    },
    franchiseNameTruncate: {
        display: '-webkit-box',
        WebkitBoxOrient: 'vertical',
        WebkitLineClamp: '2',
    },
    franchiseNameMobile: {
        paddingTop: '0.75rem',
        paddingBottom: '0.75rem',
        paddingLeft: '1.5rem',
        paddingRight: '1.5rem',
        marginTop: '1rem',
        textAlign: 'center',
        borderTop: `1px solid ${colors.grey200}`,
        borderBottom: `1px solid ${colors.grey200}`,
        '@media (min-width: 768px)': {
            display: 'none',
        },
    },
    franchiseNameTablet: {
        display: 'none',
        '@media (min-width: 768px)': {
            display: 'flex',
            textAlign: 'left',
            paddingLeft: '0.75rem',
            alignSelf: 'center',
            marginLeft: 'auto',
            marginRight: 'auto',
        },
        '@media (min-width: 1024px)': {
            display: 'none',
        },
    },
    menuText: {
        fontSize: '0.875rem',
        lineHeight: '1.25rem',
        '@media (min-width: 1024px)': {
            display: 'block',
        },
    },
};

export default Header;
